import React from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslate } from '@tolgee/react';

const SKELETON_ROW_COUNT = 6;

const TrackRowSkeleton: React.FC = () => (
  <div
    className="d-flex align-items-center gap-2 p-2 pe-5 rounded border border-light bg-white"
    aria-hidden="true"
  >
    <ContentLoader
      speed={1.6}
      width={40}
      height={40}
      viewBox="0 0 40 40"
      backgroundColor="#f3f3f3"
      foregroundColor="#e6e6e6"
      style={{ flexShrink: 0 }}
    >
      <rect x="0" y="0" rx="4" ry="4" width="40" height="40" />
    </ContentLoader>
    <ContentLoader
      speed={1.6}
      width="100%"
      height={36}
      viewBox="0 0 240 36"
      preserveAspectRatio="none"
      backgroundColor="#f3f3f3"
      foregroundColor="#e6e6e6"
      style={{ flexGrow: 1 }}
    >
      <rect x="0" y="4" rx="4" ry="4" width="160" height="12" />
      <rect x="0" y="22" rx="4" ry="4" width="100" height="10" />
    </ContentLoader>
    <ContentLoader
      speed={1.6}
      width={32}
      height={12}
      viewBox="0 0 32 12"
      backgroundColor="#f3f3f3"
      foregroundColor="#e6e6e6"
      style={{ flexShrink: 0 }}
    >
      <rect x="0" y="0" rx="4" ry="4" width="32" height="12" />
    </ContentLoader>
  </div>
);

export interface AudioTrack {
  audio_id: string;
  audio_type: 'music' | 'original_sound';
  title?: string | null;
  display_artist?: string | null;
  duration_in_ms?: number | null;
  cover_artwork_thumbnail_uri?: string | null;
  download_url?: string | null;
  ig_username?: string | null;
  profile_picture_url?: string | null;
}

interface Props {
  tracks: AudioTrack[];
  isLoading: boolean;
  selectedAudioId?: string | null;
  onSelect: (track: AudioTrack) => void;
  onDeselect: () => void;
}

const formatDuration = (ms?: number | null) => {
  if (!ms) return '';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const TrackList: React.FC<Props> = ({
  tracks,
  isLoading,
  selectedAudioId,
  onSelect,
  onDeselect
}) => {
  const { t } = useTranslate();

  if (isLoading) {
    return (
      <div className="d-flex flex-column gap-1">
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
          <TrackRowSkeleton key={idx} />
        ))}
      </div>
    );
  }

  if (!isLoading && tracks.length === 0) {
    return (
      <div className="text-muted small py-3 text-center">
        {t('lomavisCreator.IG.music.noResults')}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-1">
      {tracks.map((track) => {
        const isSelected = track.audio_id === selectedAudioId;
        return (
          <div
            key={track.audio_id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(track)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(track);
              }
            }}
            className={`d-flex align-items-center gap-2 p-2 pe-5 rounded text-start border cursor-pointer ${
              isSelected
                ? 'border-primary bg-light-primary'
                : 'border-light bg-white'
            }`}
          >
            {track.cover_artwork_thumbnail_uri ? (
              <img
                src={track.cover_artwork_thumbnail_uri}
                alt=""
                width={40}
                height={40}
                style={{ objectFit: 'cover', borderRadius: 4 }}
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 4,
                  background: '#eee'
                }}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="fa-duotone fa-music text-muted"></i>
              </div>
            )}
            <div className="d-flex flex-column flex-grow-1 overflow-hidden pe-3">
              <span className="text-truncate fw-semibold">
                {track.title || track.ig_username || '—'}
              </span>
              <span className="text-truncate text-muted small">
                {track.display_artist || track.ig_username || ''}
              </span>
            </div>
            {isSelected && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-muted"
                aria-label={t('lomavisCreator.IG.music.clear')}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeselect();
                }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
            <span
              className="text-muted small text-nowrap"
              style={{ minWidth: 40, textAlign: 'right' }}
            >
              {formatDuration(track.duration_in_ms)}
            </span>
          </div>
        );
      })}
    </div>
  );
};
